import { __awaiter } from 'tslib';
import { makeCancelable } from '@sketch/utils';

/**
 * Preload a list of images so that the user can see them instantly once added to the DOM.
 * You can listen to the loading progress of the images using imagePreloader.addOnProgressListener(...)
 * and wait for all the images to have finished loading using imagePreloader.whenReady() promise.
 */
class ImagesPreloader {
    constructor(imagesToPreload, resolveImageSrc) {
        this.wasDisposed = false;
        this.progressListener = [];
        /**
         * Keep a pointer to the preloaded images in memory so they are not garbage collected
         * which would defeats the point of preloading the images.
         */
        this.imageCache = {};
        /**
         * Returns a promise that resolves once all the images have loaded.
         * If some images requests have failed, the promise will still resolve
         * but the returned object will contain the number of failed requests.
         *
         * If some image requests finish after the instance is disposed, the promise will
         * reject.
         */
        this.whenReady = () => {
            this.ensureNotAlreadyDisposed();
            return this.globalPromise.then(() => ({
                total: this.numberOfImages,
                loaded: this.numberImageLoadSuccess,
                failed: this.numberImageLoadError,
            }));
        };
        /**
         * Subscribe a callback that will be called with the percentage of progress
         * each time an image finishes loading (whether with success or error).
         */
        this.addOnProgressListener = (listener) => {
            this.ensureNotAlreadyDisposed();
            this.progressListener.push(listener);
        };
        /**
         * Remove subscribed callback.
         */
        this.removeOnProgressListener = (listenerToRemove) => {
            this.ensureNotAlreadyDisposed();
            this.progressListener = this.progressListener.filter(listener => listenerToRemove !== listener);
        };
        this.emitProgress = () => {
            const numberOfCompletedImages = this.numberImageLoadSuccess + this.numberImageLoadError;
            const progress = this.numberOfImages === 0
                ? 1
                : numberOfCompletedImages / this.numberOfImages;
            this.progressListener.forEach(listener => {
                listener(progress);
            });
        };
        this.handleImageLoadSuccess = () => {
            this.numberImageLoadSuccess += 1;
            this.emitProgress();
        };
        this.handleImageLoadError = () => {
            this.numberImageLoadError += 1;
            this.emitProgress();
        };
        /**
         * Function to call to clean things up and avoid memory leaks.
         * Make instance invalid, cancel whenReady() promise, remove progress listeners and clear imageCache.
         */
        this.dispose = () => {
            this.ensureNotAlreadyDisposed();
            this.wasDisposed = true;
            this.globalPromise.cancel();
            this.progressListener = [];
            /**
             * Release all images so they can be garbage collected.
             * Once garbage collected, the image will need to be re-downloaded/decoded.
             */
            this.imageCache = {};
        };
        this.resolveImageSrc = resolveImageSrc;
        this.numberOfImages = imagesToPreload.length;
        this.numberImageLoadSuccess = 0;
        this.numberImageLoadError = 0;
        if (imagesToPreload.length) {
            const imagesLoadPromises = this.preloadImages(imagesToPreload);
            this.globalPromise = makeCancelable(Promise.all(imagesLoadPromises));
        }
        else {
            // Nothing to preload, set it ready automatically
            this.globalPromise = makeCancelable(Promise.resolve({
                total: this.numberOfImages,
                loaded: this.numberImageLoadSuccess,
                failed: this.numberImageLoadError,
            }));
        }
    }
    preloadImages(imagesToPreload) {
        const imagesLoadPromises = imagesToPreload.map(imageToPreload => {
            return this.loadImage(imageToPreload)
                .then(this.handleImageLoadSuccess)
                .catch(this.handleImageLoadError);
        });
        return imagesLoadPromises;
    }
    loadImage(imageToPreload) {
        const sourceSet = imageToPreload.srcset
            .map(srcSetItem => `${this.resolveImageSrc(srcSetItem.path)} ${srcSetItem.scale}x`)
            .join(', ');
        const image = new Image();
        image.srcset = sourceSet;
        image.src = this.resolveImageSrc(imageToPreload.src);
        /**
         * It is important to store the image instance in the memory cache to avoid it being
         * garbage collected! If garbage collected, we would need to re-download the image
         * which defeats the point of preloading.
         */
        this.imageCache[image.src] = image;
        /**
         * Wait for image to be loaded and also decoded when we can.
         * We tried to rely only on image.decode() but this does not seem to be reliable
         * and fails when too many images are requested on Chrome (v99)
         */
        return new Promise((resolve, reject) => {
            image.onload = () => __awaiter(this, void 0, void 0, function* () {
                if (!image.decode) {
                    /*
                     * We are expected to run in browsers where image.decode is supported, but let's not break
                     * the whole experience because of image preloading in case it's not. Also, during testing,
                     * jsdom does not support it.
                     */
                    resolve();
                }
                /**
                 * Try to wait for image to also be decoded. For some reasons Chrome does not always succeed though
                 * when there are a lot of images to load at the same time. In that case image.decode() fails with
                 * an error but the images seems to already be preload correctly.
                 */
                try {
                    // image.decode() will resolve when the image has been loaded and decoded.
                    yield image.decode();
                    resolve();
                }
                catch (error) {
                    // In any case resolve since the image is loaded
                    resolve();
                }
            });
            image.onerror = () => {
                reject();
            };
        });
    }
    /** Throw an error if instance has already been disposed */
    ensureNotAlreadyDisposed() {
        if (this.wasDisposed) {
            throw new Error('ImagesPreloader instance has been disposed');
        }
    }
}

export { ImagesPreloader };
